import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Admin tool: same-name duplicate items → pick MAIN → dry-run → link duplicate as inactive (no delete).
export default function DuplicateItemsDialog({ open, onClose, onDone }) {
  const [groups, setGroups] = useState(null);
  const [keep, setKeep] = useState({});
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPlan(null);
    api.get("/admin/items/duplicates").then(({ data }) => {
      setGroups(data);
      setKeep(Object.fromEntries(data.map((g, i) => [i, g.items[0].id])));
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [open]);

  const run = async (gi, dupId, dry) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/items/dedupe?dry_run=${dry}`, { keep_id: keep[gi], dup_id: dupId });
      if (dry) setPlan({ gi, dupId, ...data });
      else { toast.success(`Merged · MAIN stock ab ${data.main_stock_now}`); setPlan(null); onDone?.(); onClose(); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-white rounded-sm max-h-[85vh] overflow-y-auto" data-testid="duplicates-dialog">
        <DialogHeader><DialogTitle className="font-heading">Duplicate items</DialogTitle></DialogHeader>
        {!groups ? <div className="text-slate-400 text-sm">Loading…</div> : groups.length === 0 ? (
          <div className="text-slate-400 text-sm" data-testid="duplicates-none">Koi duplicate nahi mila.</div>
        ) : groups.map((g, gi) => (
          <div key={gi} className="surface-2 p-3 rounded-sm space-y-2" data-testid={`dup-group-${gi}`}>
            <div className="font-heading text-sm">{g.name}</div>
            {g.items.map((it) => (
              <label key={it.id} className="flex items-center gap-3 text-xs border-b border-slate-800 py-1.5" data-testid={`dup-item-${it.id}`}>
                <input type="radio" name={`keep-${gi}`} checked={keep[gi] === it.id} onChange={() => setKeep({ ...keep, [gi]: it.id })} data-testid={`dup-keep-${it.id}`} />
                <span className="flex-1 min-w-0 truncate">{it.name} <span className="text-slate-500">· {it.category}</span></span>
                <span className="font-mono-num text-slate-300">stock {it.current_stock} {it.unit}</span>
                <span className="font-mono-num text-slate-300">₹{it.sale_price}</span>
                <span className="font-mono-num text-slate-400">{it.orders} orders</span>
                {keep[gi] === it.id ? <span className="text-emerald-400 font-mono-num">MAIN</span> : (
                  <button type="button" disabled={busy} onClick={() => run(gi, it.id, true)} className="h-7 px-2 border border-amber-500 text-amber-200 rounded-sm font-mono-num" data-testid={`dup-dryrun-${it.id}`}>DRY-RUN</button>
                )}
              </label>
            ))}
            {plan && plan.gi === gi && (
              <div className="border border-dashed border-amber-500/60 p-2 rounded-sm text-xs space-y-1" data-testid="dup-plan">
                <table className="w-full font-mono-num text-[11px]" data-testid="dup-plan-table">
                  <thead><tr className="text-slate-500"><th className="text-left">ITEM</th><th className="text-right">STOCK BEFORE</th><th className="text-right">STOCK AFTER</th><th className="text-right">ORDERS BEFORE</th><th className="text-right">ORDERS AFTER</th></tr></thead>
                  <tbody>
                    <tr><td>MAIN · {plan.keep.name}</td><td className="text-right">{plan.keep.stock}</td><td className="text-right text-emerald-300" data-testid="dup-plan-main-after">{plan.after.main_stock}</td><td className="text-right">{plan.keep.orders}</td><td className="text-right text-emerald-300">{plan.after.main_orders}</td></tr>
                    <tr><td>DUP · {plan.duplicate.name}</td><td className="text-right">{plan.duplicate.stock}</td><td className="text-right">0</td><td className="text-right">{plan.duplicate.orders}</td><td className="text-right">0</td></tr>
                  </tbody>
                </table>
                <div className="text-slate-400">{plan.change}. Purane orders ka item naam waisa hi rahega.</div>
                <button type="button" disabled={busy} onClick={() => run(gi, plan.dupId, false)} className="h-8 px-3 bg-amber-500 text-slate-900 rounded-sm font-heading text-xs" data-testid="dup-apply">APPLY — MERGE INTO MAIN</button>
              </div>
            )}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
